import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Target, CheckCircle2 } from 'lucide-react';
import { formatUsd } from '@/lib/rebatesData';

const fmtDate = (d) =>
  new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(d);

/**
 * Home Depot Pro Xtra rebate progress — pre-tax HD spend this rebate period
 * vs the program's qualifying threshold. Opt-in via Settings → Defaults;
 * rendered only when enabled there.
 */
const ProXtraCard = ({ progress }) => {
  if (!progress) return null;
  const { spend, threshold, remaining, pct, qualified, receiptCount, periodStart, periodEnd } =
    progress;
  return (
    <Card
      className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))]"
      data-testid="pro-xtra-card"
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2">
            {qualified ? (
              <CheckCircle2 size={15} className="text-[hsl(var(--success))]" />
            ) : (
              <Target size={15} className="text-[hsl(var(--primary))]" />
            )}
            <span className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
              Home Depot Pro Xtra
            </span>
          </div>
          <Badge
            variant="outline"
            className={
              qualified
                ? 'border-[hsl(var(--success))]/40 text-[hsl(var(--success))] uppercase text-[10px] tracking-[0.16em]'
                : 'border-border text-muted-foreground uppercase text-[10px] tracking-[0.16em]'
            }
          >
            {fmtDate(periodStart)} – {fmtDate(periodEnd)}
          </Badge>
        </div>

        <div className="mt-2 flex items-baseline justify-between gap-3">
          <div
            className="text-xl font-semibold text-foreground tabular-nums"
            data-testid="pro-xtra-spend"
          >
            {formatUsd(spend)}
            <span className="text-xs font-normal text-muted-foreground">
              {' '}
              / {formatUsd(threshold)}
            </span>
          </div>
          <div className="text-xs text-right" data-testid="pro-xtra-status">
            {qualified ? (
              <span className="text-[hsl(var(--success))] font-semibold">
                Qualified — rebate unlocked
              </span>
            ) : (
              <span className="text-muted-foreground">
                <span className="text-foreground font-semibold tabular-nums">
                  {formatUsd(remaining)}
                </span>{' '}
                to qualify
              </span>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-3 h-2 rounded-full bg-[hsl(var(--muted))] overflow-hidden">
          <div
            className={
              qualified
                ? 'h-full rounded-full bg-[hsl(var(--success))] transition-all'
                : 'h-full rounded-full bg-[hsl(var(--primary))] transition-all'
            }
            style={{ width: `${Math.max(2, Math.round(pct * 100))}%` }}
            data-testid="pro-xtra-bar"
          />
        </div>

        <div className="mt-2 text-[10px] text-muted-foreground">
          Pre-tax Home Depot spend this rebate period · {receiptCount} receipt
          {receiptCount === 1 ? '' : 's'} · based on scanned receipts
        </div>
      </CardContent>
    </Card>
  );
};

export default ProXtraCard;
